import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EmailIcon from "@mui/icons-material/Email";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import { StatusChip } from "./AnalyticsDataTable";

const SUBJECTS = ["Maths", "English", "Verbal Reasoning", "Non-Verbal Reasoning"];

// The cohort bands use their own vocabulary; map them onto the shared chip palette.
const BAND_STATUS = { weak: "high", medium: "medium", strong: "on_track" };

// `weak_below` — who counts as needing help. 40 is the config default (config/analytics.php),
// so it is labelled as such; the wider nets are what an admin reaches for when a topic needs a
// class big enough to be worth running.
const THRESHOLDS = [
  { value: 40, label: "Below 40% (default)" },
  { value: 50, label: "Below 50%" },
  { value: 60, label: "Below 60%" },
  { value: 70, label: "Below 70%" },
];

function defaultCampaignBody(cohort) {
  return [
    `<p>We have been reviewing your child's recent worksheet results.</p>`,
    `<p>They are finding <strong>${cohort.subtopic_name}</strong> difficult at the moment &mdash;`,
    ` an area that comes up regularly in the 11+.</p>`,
    `<p>We are running a short revision class focused on exactly this topic. It is a small group,`,
    ` so there is time for questions and worked examples.</p>`,
    `<p>Reply to this email or log in to book a place.</p>`,
  ].join("");
}

export default function WeakTopicCohorts() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [minStudents, setMinStudents] = useState(1);
  const [weakBelow, setWeakBelow] = useState(40);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [campaignFor, setCampaignFor] = useState(null);
  const [campaignSubject, setCampaignSubject] = useState("");
  const [campaignBody, setCampaignBody] = useState("");
  const [sending, setSending] = useState(false);
  const [creatingKey, setCreatingKey] = useState(null);

  // The filters that define *which* students are in a cohort. Both actions below resend them, so
  // the server acts on the same group the table is showing — a cohort at a 60% threshold is a
  // strictly larger set than the same cohort at 40%.
  const cohortFilters = useMemo(() => {
    const f = { min_students: minStudents, weak_below: weakBelow };
    if (subject) f.subject = subject;
    return f;
  }, [subject, minStudents, weakBelow]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("admin/analytics/topic-cohorts", { params: cohortFilters });
      setData(res.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load weak topic cohorts");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [cohortFilters]);

  useEffect(() => { load(); }, [load]);

  const cohorts = useMemo(() => data?.cohorts || [], [data]);
  const summary = data?.summary || {};

  const openCampaign = (cohort) => {
    setCampaignFor(cohort);
    setCampaignSubject(`Extra help with ${cohort.subtopic_name}`);
    setCampaignBody(defaultCampaignBody(cohort));
  };

  const sendCampaign = async () => {
    if (!campaignFor) return;
    try {
      setSending(true);
      const { subject: cohortSubject, ...rest } = cohortFilters;
      const res = await api.post("admin/analytics/topic-cohorts/campaign", {
        ...rest,
        // `subject` is the email's subject line on this endpoint, so the cohort's school subject
        // travels as `cohort_subject`.
        ...(cohortSubject ? { cohort_subject: cohortSubject } : {}),
        key: campaignFor.key,
        subject: campaignSubject,
        body_html: campaignBody,
      });
      toast.success(res.data?.message || "Campaign queued");
      setCampaignFor(null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to queue campaign");
    } finally {
      setSending(false);
    }
  };

  const createCourse = async (cohort) => {
    try {
      setCreatingKey(cohort.key);
      const res = await api.post("admin/analytics/topic-cohorts/create-course", {
        ...cohortFilters,
        key: cohort.key,
      });
      const courseId = res.data?.data?.course_id;
      toast.success(res.data?.message || "Draft revision course created");
      if (courseId) navigate(`/admin/course/${courseId}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to create revision course");
    } finally {
      setCreatingKey(null);
    }
  };

  return (
    <AnalyticsPageShell
      title="Weak Topics"
      subtitle={
        "Subtopics students are struggling with, from graded worksheet results. Create a revision "
        + "class for a group, or email them about one."
      }
      filters={(
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Subject</InputLabel>
              <Select label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)}>
                <MenuItem value="">All subjects</MenuItem>
                {SUBJECTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Threshold</InputLabel>
              <Select label="Threshold" value={weakBelow} onChange={(e) => setWeakBelow(Number(e.target.value))}>
                {THRESHOLDS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Min students</InputLabel>
              <Select
                label="Min students"
                value={minStudents}
                onChange={(e) => setMinStudents(Number(e.target.value))}
              >
                {[1, 2, 5, 10, 20].map((n) => <MenuItem key={n} value={n}>{n}+</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
    >
      {!loading && cohorts.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {summary.subtopics_assessed} subtopic(s) assessed &middot;{" "}
          {summary.students_needing_support} student(s) below {summary.weak_below}%
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ ...CARD_SX, overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Topic</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Subtopic</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Avg %</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Students</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Band</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cohorts.map((c) => (
                <TableRow key={c.key} hover>
                  <TableCell>{c.subject || "-"}</TableCell>
                  <TableCell>{c.topic_name || "-"}</TableCell>
                  <TableCell>{c.subtopic_name}</TableCell>
                  <TableCell align="right">{c.avg_percent}%</TableCell>
                  <TableCell align="right">
                    <Tooltip title={`${c.students_assessed} assessed on this subtopic`}>
                      <span>{c.student_count}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell><StatusChip status={BAND_STATUS[c.band] || c.band} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Create a draft revision course for this group. It stays hidden until you approve it.">
                        <span>
                          <Button
                            size="small" variant="outlined" startIcon={<AutoAwesomeIcon />}
                            disabled={creatingKey === c.key}
                            onClick={() => createCourse(c)}
                          >
                            Create course
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={c.student_count ? "Email these students" : "No students below the threshold"}>
                        <span>
                          <Button
                            size="small" variant="text" startIcon={<EmailIcon />}
                            disabled={!c.student_count}
                            onClick={() => openCampaign(c)}
                          >
                            Email
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {cohorts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 3 }}>
                    No weak topics found for this filter
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!campaignFor} onClose={() => setCampaignFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Email {campaignFor?.student_count} student(s) about {campaignFor?.subtopic_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Everyone below {weakBelow}% on this subtopic. Sent to the parent where we have one, and
            skips anyone who opted out of engagement email.
          </Typography>
          <TextField
            fullWidth size="small" label="Subject" value={campaignSubject}
            onChange={(e) => setCampaignSubject(e.target.value)} sx={{ mb: 2 }}
          />
          <TextField
            fullWidth multiline minRows={8} size="small" label="Body (HTML)"
            value={campaignBody} onChange={(e) => setCampaignBody(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignFor(null)}>Cancel</Button>
          <Button
            variant="contained" disabled={sending || !campaignSubject || !campaignBody}
            onClick={sendCampaign}
          >
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </AnalyticsPageShell>
  );
}
